import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantsApi } from '../../services/api';
import { PlusIcon, SearchIcon } from '../../utils/icons';
import { formatDate } from '../../utils/formatters';
import Modal from '../../components/Modal';
import './Tenants.css';

const EMPTY_FORM = {
  name: '', slug: '', address: '', email: '', contact: '',
  max_users: 5, leads_module_enabled: true, booking_module_enabled: false,
};

export default function SuperAdminTenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Modal state
  const [showCreate, setShowCreate] = useState(false);

  const fetchTenants = useCallback(async () => {
    try {
      setError('');
      const res = await tenantsApi.list();
      setTenants(res.data);
    } catch (err) {
      setError('Failed to load organisations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const filtered = tenants.filter((t) =>
    !search ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase()) ||
    t.email.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = tenants.filter((t) => t.is_active).length;
  const totalMaxUsers = tenants.reduce((a, t) => a + t.max_users, 0);
  const modulesEnabled = tenants.filter((t) => t.leads_module_enabled || t.booking_module_enabled).length;

  const handleCreated = (newTenant) => {
    setShowCreate(false);
    navigate(`/super-admin/tenants/${newTenant.id}/users`);
  };

  if (loading) {
    return (
      <div className="tenants-page">
        <div className="tenants-loading">
          <div className="tenants-loading-spinner" />
          <p>Loading organisations…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tenants-page">
      <div className="page-header animate-fade-up">
        <div className="page-header-top">
          <div>
            <p className="tenants-label section-label">Super Admin</p>
            <h1 className="page-title">Organisations</h1>
            <p className="page-subtitle">{tenants.length} tenants on the platform</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              <PlusIcon /> New Organisation
            </button>
          </div>
        </div>
      </div>

      {error && <div className="tenants-error animate-fade-up">{error}</div>}

      {/* Stats */}
      <div className="tenant-stats stagger animate-fade-up" style={{ animationDelay: '40ms' }}>
        <div className="tenant-stat-card">
          <div className="tenant-stat-value">{tenants.length}</div>
          <div className="tenant-stat-label">Total Orgs</div>
        </div>
        <div className="tenant-stat-card">
          <div className="tenant-stat-value">{activeCount}</div>
          <div className="tenant-stat-label">Active</div>
        </div>
        <div className="tenant-stat-card">
          <div className="tenant-stat-value">{totalMaxUsers}</div>
          <div className="tenant-stat-label">Max Users</div>
        </div>
        <div className="tenant-stat-card">
          <div className="tenant-stat-value">{modulesEnabled}</div>
          <div className="tenant-stat-label">Modules On</div>
        </div>
      </div>

      {/* Search + table */}
      <div className="animate-fade-up" style={{ animationDelay: '80ms' }}>
        <div className="tenant-search-wrap">
          <span className="tenant-search-icon"><SearchIcon /></span>
          <input
            className="input tenant-search"
            placeholder="Search organisations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 14 }}>
          <table className="tenant-table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Subdomain</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Max Users</th>
                <th>Modules</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tenant-empty">
                    {search ? 'No organisations match your search.' : 'No organisations yet.'}
                  </td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr
                    key={t.id}
                    className="tenant-row-clickable"
                    onClick={() => navigate(`/super-admin/tenants/${t.id}`)}
                  >
                    <td>
                      <div className="tenant-name-cell">
                        <div className="tenant-avatar">
                          {t.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                        </div>
                        <span className="tenant-name">{t.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="tenant-subdomain">{t.slug}.realtycrm.com</span>
                    </td>
                    <td className="tenant-email">{t.email}</td>
                    <td className="tenant-contact">{t.contact}</td>
                    <td className="tenant-number">{t.max_users}</td>
                    <td>
                      <div className="module-badges">
                        {t.leads_module_enabled && <span className="module-badge module-badge--leads">Leads</span>}
                        {t.booking_module_enabled && <span className="module-badge module-badge--booking">Booking</span>}
                        {!t.leads_module_enabled && !t.booking_module_enabled && (
                          <span className="module-badge module-badge--none">None</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${t.is_active ? 'closed' : 'lost'}`}>
                        <span className="badge-dot" />
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="tenant-date">
                      {formatDate(t.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <TenantFormModal
          title="New Organisation"
          initial={EMPTY_FORM}
          onClose={() => setShowCreate(false)}
          onSave={async (data) => {
            const res = await tenantsApi.create(data);
            handleCreated(res.data);
          }}
          submitLabel="Create & Add Users"
        />
      )}

    </div>
  );
}


/* ─── Tenant Form Modal ──────────────────────────────────────────────────── */

function TenantFormModal({ title, initial, isEdit, onClose, onSave, submitLabel }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    slug: initial.slug || '',
    address: initial.address || '',
    email: initial.email || '',
    contact: initial.contact || '',
    max_users: initial.max_users || 5,
    leads_module_enabled: initial.leads_module_enabled ?? true,
    booking_module_enabled: initial.booking_module_enabled ?? false,
    is_active: initial.is_active ?? true,
  });
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleName = (val) => {
    setForm((f) => ({
      ...f,
      name: val,
      slug: slugTouched ? f.slug : val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (isEdit) {
        delete payload.slug;
      }
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}

            <div className="form-row">
              <div className="form-group">
                <label className="section-label">Name *</label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => handleName(e.target.value)}
                  placeholder="Organisation name"
                  required
                />
              </div>
              <div className="form-group">
                <label className="section-label">Slug *</label>
                <input
                  className="input"
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: e.target.value })); }}
                  placeholder="url-safe-slug"
                  required
                  disabled={isEdit}
                  pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
                  title="Lowercase letters, numbers, and hyphens only"
                />
                {!isEdit && <span className="form-hint">{form.slug}.realtycrm.com</span>}
              </div>
            </div>

            <div className="form-group">
              <label className="section-label">Address *</label>
              <input
                className="input"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Organisation address"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="section-label">Email *</label>
                <input
                  className="input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="contact@org.com"
                  required
                />
              </div>
              <div className="form-group">
                <label className="section-label">Contact *</label>
                <input
                  className="input"
                  type="tel"
                  value={form.contact}
                  onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                  placeholder="+91 98765 43210"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="section-label">Max Users *</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={500}
                  value={form.max_users}
                  onChange={(e) => setForm((f) => ({ ...f, max_users: parseInt(e.target.value) || 1 }))}
                  required
                />
              </div>
              {isEdit && (
                <div className="form-group">
                  <label className="section-label">Status</label>
                  <div className="toggle-row">
                    <ToggleSwitch
                      checked={form.is_active}
                      onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                    />
                    <span className="toggle-label">{form.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="form-divider" />

            <div className="form-row">
              <div className="form-group">
                <label className="section-label">Leads Module</label>
                <div className="toggle-row">
                  <ToggleSwitch
                    checked={form.leads_module_enabled}
                    onChange={(v) => setForm((f) => ({ ...f, leads_module_enabled: v }))}
                  />
                  <span className="toggle-label">{form.leads_module_enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <div className="form-group">
                <label className="section-label">Booking Module</label>
                <div className="toggle-row">
                  <ToggleSwitch
                    checked={form.booking_module_enabled}
                    onChange={(v) => setForm((f) => ({ ...f, booking_module_enabled: v }))}
                  />
                  <span className="toggle-label">{form.booking_module_enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
    </Modal>
  );
}



/* ─── Toggle Switch ──────────────────────────────────────────────────────── */

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`toggle-switch ${checked ? 'toggle-switch--on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-thumb" />
    </button>
  );
}


